import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import FilmCard from "@/components/feature/FilmCard";
import { films } from "@/mocks/films";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const hotList = [...films].sort((a, b) => b.rating - a.rating).slice(0, 4);

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKeyword = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialKeyword);

  const trimmed = keyword.trim();

  useDocumentMeta({
    title: trimmed ? `搜索“${trimmed}” - 韩国AV影视资源` : "搜索影视作品 - 韩国AV",
    description: `在韩国AV影视资料库中搜索影视作品名称、题材与剧情关键词，快速查找韩国电影与电视剧资料。`,
    keywords: `搜索影视作品,韩国AV,韩国电影,韩剧,${trimmed}`,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (trimmed) {
        next.set("q", trimmed);
      } else {
        next.delete("q");
      }
      setSearchParams(next, { replace: true });
    }, 320);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed]);

  const results = useMemo(() => {
    if (!trimmed) return [];
    const lower = trimmed.toLowerCase();
    return films.filter((film) => {
      const haystack = [
        film.title,
        film.originalTitle,
        film.genres.join(" "),
        film.tags.join(" "),
        film.director,
        film.cast.join(" "),
        film.synopsis,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(lower);
    });
  }, [trimmed]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (trimmed) next.set("q", trimmed);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 pb-4 pt-6 md:px-6 lg:px-8">
        <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-[12px] text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <div className="mt-4 overflow-hidden rounded-lg border border-background-200/90 bg-primary-950 p-5 md:p-7">
          <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent-400">Search Archive</span>
          <h1 className="mt-2 font-heading text-[24px] font-semibold text-background-50 md:text-[28px]">
            {trimmed ? `搜索结果：${trimmed}` : "搜索影视作品"}
          </h1>
          <p className="mt-2 max-w-2xl text-[13px] leading-6 text-primary-200">
            支持作品名称、原名、题材、导演、主演与剧情关键词的模糊匹配。输入关键词后结果会自动刷新。
          </p>

          <form onSubmit={submit} className="mt-5 flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-primary-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：悬疑、汉江之冬、时间循环"
                aria-label="搜索关键词"
                className="h-11 w-full rounded-md border border-primary-700 bg-primary-900/70 pl-10 pr-4 text-sm text-background-50 outline-none transition-colors placeholder:text-primary-400 focus:border-accent-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 whitespace-nowrap rounded-md bg-accent-500 px-5 text-[13px] font-medium text-primary-950 transition-colors hover:bg-accent-400"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-primary-300">
            <span>热门关键词：</span>
            {["悬疑", "家庭", "科幻", "治愈", "古装"].map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setKeyword(word)}
                className="rounded border border-primary-700 bg-primary-900/50 px-2 py-1 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                {word}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12px] text-foreground-500">
            {trimmed ? (
              <>
                共找到 <span className="font-medium text-foreground-900">{results.length}</span> 部与「
                <span className="text-primary-700">{trimmed}</span>」相关的作品
              </>
            ) : (
              <>输入关键词开始查找，或浏览下方热门作品</>
            )}
          </p>
          {trimmed && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-[11px] text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-700"
            >
              <i className="ri-close-line" />
              清空关键词
            </button>
          )}
        </div>

        {trimmed && results.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {results.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
        )}

        {trimmed && results.length === 0 && (
          <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-search-eye-line text-xl" />
            </span>
            <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">没有找到相关作品</h2>
            <p className="mt-1.5 text-[12px] leading-6 text-foreground-500">
              试试更短的关键词，例如只输入作品名中的两个字，或者直接浏览全部作品。
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览全部作品
              </Link>
              <button
                type="button"
                onClick={() => setKeyword("悬疑")}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-[12px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                试试「悬疑」
              </button>
            </div>
          </div>
        )}

        {!trimmed && (
          <div className="mt-8">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent-700">Popular Now</span>
                <h2 className="mt-1.5 font-heading text-[19px] font-semibold text-foreground-950">热门影视推荐</h2>
              </div>
              <Link to="/category" className="flex items-center gap-1 text-[12px] text-foreground-500 hover:text-primary-700">
                全部分类
                <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {hotList.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          </div>
        )}

        {trimmed && results.length > 0 && (
          <div className="mt-8 rounded-lg border border-background-200/90 bg-background-100 p-5">
            <h2 className="text-[13px] font-semibold text-foreground-900">没找到想要的？</h2>
            <p className="mt-1.5 text-[12px] leading-6 text-foreground-500">
              本站作品资料持续补充中。如果你的关键词涉及尚未收录的作品，欢迎通过联系页告诉我们。
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-[12px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-mail-send-line text-sm" />
                提交资料补充
              </Link>
              <Link
                to="/special"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-[12px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-bookmark-3-line text-sm" />
                看看专题合集
              </Link>
            </div>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}